import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useStickyHeaderHeight } from '@/hooks/useStickyHeaderHeight';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styles from "./Header.module.css";
import dropdownStyles from '../../ui/DropdownMenu/DropdownMenu.module.css';
import { useAuth } from '@/contexts/shared/AuthContext';
import { useAdminAccess } from '@/hooks/useAdminAccess';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useTheme } from '@/contexts/shared/ThemeContext';
import { useImpersonation } from '@/hooks/admin/AuditManagement/useImpersonation';
import { useInstalledLanguages } from '@/hooks/useInstalledLanguages';
import { useLanguage, tr } from '@/i18n';
import { roleLabel } from '@/i18n/roleLabels';
import { getAvatarData } from '@/utils/avatar';

import SettingsIcon from '../../../../public/assets/icons/settings.svg?react';
import LanguageIcon from '../../../../public/assets/icons/language.svg?react';
import ModeIcon from '../../../../public/assets/icons/sun.svg?react';
import MoonIcon from '../../../../public/assets/icons/moon.svg?react';
import LogoutIcon from '../../../../public/assets/icons/logout.svg?react';
import ProfileIcon from '../../../../public/assets/icons/user.svg?react';
import LockIcon from '../../../../public/assets/icons/lock.svg?react';
import CalendarIcon from '../../../../public/assets/icons/calendar-simple.svg?react';
import ChatBubbleIcon from '../../../../public/assets/icons/chat.svg?react';

import { GlobalSearch } from '../GlobalSearch/GlobalSearch';
import { BrandLogo } from '../BrandLogo/BrandLogo';
// The Admin menu is the same mega-menu the modern top bar uses, so admins get one navigation in both interfaces.
const AdminMenu = lazy(() => import('@/components/shared/TopBar/AdminMenu').then(m => ({ default: m.AdminMenu })));
import { DropdownItem, DropdownMenu } from '@/components/ui/DropdownMenu/DropdownMenu';
import { ConfirmModal } from '../ConfirmModal/ConfirmModal';
import { CHANGE_PASSWORD_PATH, signsInWithPassword } from '@/auth/passwordChange';
import { NotificationBell } from '../NotificationBell/NotificationBell';
import { DM_QUERY_KEYS, MessagingApi } from '@/services/messaging.service';

interface HeaderProps {
  /** When provided, a hamburger button is shown on mobile to toggle the nav rail. */
  onMobileMenuClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMobileMenuClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);

  const { theme, setTheme: setContextTheme, allowedThemes } = useTheme();

  // Language switching — installed languages (names only, never flags)
  const { languages } = useInstalledLanguages();
  const { locale, setLocale } = useLanguage();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);

  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  useStickyHeaderHeight(headerRef);  // phones: pinned page content stops below the sticky header
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  const langButtonRef = useRef<HTMLButtonElement>(null);

  const { user, logout, isImpersonating } = useAuth();
  const adminAccess = useAdminAccess();
  const { returnToAccount, isSubmitting } = useImpersonation();

  // Unread direct-messages badge: polled every 60s, plus live invalidation
  // from the messaging socket (useMessagingSocket) on every incoming event.
  const { data: dmUnread } = useQuery({
    queryKey: DM_QUERY_KEYS.unread,
    queryFn: MessagingApi.unreadCount,
    refetchInterval: 60_000,
    enabled: !!user,
  });
  const dmBadgeCount = dmUnread?.threads ?? 0;

  const originalAdminName = localStorage.getItem('original_admin_name') || tr("tenant.users.role_admin", "Administrator");

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
  const toggleSettingsMenu = () => setIsSettingsMenuOpen(!isSettingsMenuOpen);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        if (!target.closest(`.${dropdownStyles.menu}`)) {
          setIsDropdownOpen(false);
        }
      }
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target as Node)) {
        if (!(event.target as HTMLElement).closest('.settings-trigger')) {
          setIsSettingsMenuOpen(false);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsSettingsMenuOpen(false);
    setIsDropdownOpen(false);
    setIsLangMenuOpen(false);
  }, [location]);

  const handleThemeChange = (newTheme: 'light' | 'dark') => {
    setContextTheme(newTheme);
  };

  const handleLogoutConfirm = () => {
    setIsLogoutModalOpen(false);
    logout();
  };

  // Installed + activated languages, names only (no flags — ever).
  const languageMenuItems: DropdownItem[] = languages.map(lang => ({
    label: lang.name,
    isSelected: locale === lang.code,
    onClick: () => setLocale(lang.code),
  }));

  if (!user) {
    return (
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <BrandLogo className={styles.logoImg} />
        </div>
      </header>
    );
  }

  const userRole = roleLabel(user.roles?.[0]?.name) || tr("audit.col_user", "User");
  const fullName = `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.username || tr("ui.user_x_a446ce", "User #{v0}", { v0: user.id });
  // Administrators — and course creators (also category-only ones), who get just their course pages.
  const hasAdminAccess = adminAccess.showAdmin;

  const hasAvatar = !!user.profileimageurl;

  // Time-of-day greeting (sun before 5pm, moon after)
  const hour = new Date().getHours();
  const greetingText = hour < 12 ? tr('ui.good_morning', 'Good Morning') : hour < 17 ? tr('ui.good_afternoon', 'Good Afternoon') : tr('ui.good_evening', 'Good Evening');
  const GreetingIcon = hour < 17 ? ModeIcon : MoonIcon;

  // Sun/moon switch shown in the header only when both themes are allowed
  const showThemeToggle = allowedThemes.includes('light') && allowedThemes.includes('dark');

  // Live avatar refresh: when the user changes their photo anywhere in the
  // app, the event carries the fresh blob URL — render it directly (immune to
  // URL/browser caching); a delete clears the override (initial shows).
  const [avatarVersion, setAvatarVersion] = useState(0);
  const [avatarOverride, setAvatarOverride] = useState<string | null>(null);
  useEffect(() => {
    const onAvatarUpdated = (e: Event) => {
      const detail = (e as CustomEvent).detail as { userId?: string | number; imgUrl?: string | null } | undefined;
      // Only react to changes to MY OWN avatar (admins can edit others').
      if (detail?.userId != null && String(detail.userId) !== String(user?.id)) return;
      setAvatarOverride(detail?.imgUrl ?? null);
      setAvatarVersion(Date.now());
    };
    window.addEventListener('avatarUpdated', onAvatarUpdated);
    return () => window.removeEventListener('avatarUpdated', onAvatarUpdated);
  }, [user?.id]);
  const { initial, color } = getAvatarData(user.firstname || user.username);

  return (
    <>
      {isImpersonating && (
        <div className={styles.impersonationBanner}>
          <div className={styles.impersonationContent}>
            <span className={styles.impersonationText}>
              {tr("ui.you_are_logged_in_as_437098", "🔒 You are logged in as")} <strong>{fullName}</strong>{tr("ui.original_user_sep", ". Original user:")}{' '}
              <strong>{originalAdminName}</strong>
            </span>
            <button
              className={styles.impersonationReturnBtn}
              onClick={returnToAccount}
              disabled={isSubmitting}
            >
              {isSubmitting ? tr("ui.returning_da3c06", "Returning...") : tr("ui.return_to_my_account", "Return to my account")}
            </button>
          </div>
        </div>
      )}

      <header ref={headerRef} className={`${styles.header} ${isImpersonating ? styles.withBanner : ''}`}>
        <div className={styles.headerContent}>

          {onMobileMenuClick && (
            <button
              className={styles.mobileMenuBtn}
              onClick={onMobileMenuClick}
              aria-label={tr("ui.open_navigation_menu", "Open navigation menu")}
              title={tr("course.tabs.menu", "Menu")}
            >
              ☰
            </button>
          )}

          <div className={styles.greeting}>
            <GreetingIcon className={styles.greetingIcon} aria-hidden="true" />
            <span className={styles.greetingText}>
              {greetingText}, {user.firstname || user.username}
            </span>
          </div>

          <div className={styles.searchWrapper}>
            <GlobalSearch className={styles.headerSearch} placeholder={tr("ui.search", "Search...")} />
          </div>

          <div className={styles.actions}>
            {showThemeToggle && (
              <div className={styles.themeToggle} role="group" aria-label={tr("ui.colour_theme", "Colour theme")}>
                <button
                  className={`${styles.toggleOpt} ${theme === 'light' ? styles.toggleActive : ''}`}
                  onClick={() => handleThemeChange('light')}
                  aria-label={tr("ui.light_mode", "Light mode")}
                  aria-pressed={theme === 'light'}
                  title={tr("ui.light_mode", "Light mode")}
                >
                  <ModeIcon aria-hidden="true" />
                </button>
                <button
                  className={`${styles.toggleOpt} ${theme === 'dark' ? styles.toggleActive : ''}`}
                  onClick={() => handleThemeChange('dark')}
                  aria-label={tr("ui.dark_mode", "Dark mode")}
                  aria-pressed={theme === 'dark'}
                  title={tr("ui.dark_mode", "Dark mode")}
                >
                  <MoonIcon aria-hidden="true" />
                </button>
              </div>
            )}

            {!isImpersonating && hasAdminAccess && (
              <div ref={settingsMenuRef}>
                <button
                  className={`${styles.actionButton} settings-trigger ${isSettingsMenuOpen ? styles.active : ''}`}
                  title={tr("tags.add.settings_section", "Settings")}
                  aria-label={tr("ui.settings_menu", "Settings Menu")}
                  onClick={toggleSettingsMenu}
                >
                  <SettingsIcon aria-hidden="true" />
                </button>
                {isSettingsMenuOpen && (
                  <Suspense fallback={null}>
                    <AdminMenu onClose={() => setIsSettingsMenuOpen(false)} />
                  </Suspense>
                )}
              </div>
            )}

            {languages.length > 1 && (
              <div>
                <button
                  ref={langButtonRef}
                  className={`${styles.actionButton} ${isLangMenuOpen ? styles.active : ''}`}
                  title={tr("tags.import.lang_label", "Language")}
                  aria-label={tr("ui.change_language", "Change language")}
                  aria-expanded={isLangMenuOpen}
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                >
                  <LanguageIcon aria-hidden="true" />
                </button>
                <DropdownMenu
                  isOpen={isLangMenuOpen}
                  onClose={() => setIsLangMenuOpen(false)}
                  items={languageMenuItems}
                  triggerRef={langButtonRef}
                  position="bottom"
                  align="right"
                  className={styles.langDropdown}
                />
              </div>
            )}

            <button className={styles.actionButton} title={tr("ui.calendar", "Calendar")} aria-label={tr("ui.calendar", "Calendar")} onClick={() => navigate('/user/calendar')}>
              <CalendarIcon aria-hidden="true" />
            </button>

            <button
              className={styles.actionButton}
              title={tr("ui.messages", "Messages")}
              aria-label={tr("ui.messages", "Messages")}
              onClick={() => navigate('/user/messages')}
            >
              <ChatBubbleIcon aria-hidden="true" />
              {dmBadgeCount > 0 && (
                <span className={styles.notificationBadge}>
                  {dmBadgeCount > 99 ? '99+' : dmBadgeCount}
                </span>
              )}
            </button>

            <NotificationBell />
          </div>

          <div className={styles.profile} ref={dropdownRef}>
            <button
              className={styles.profileTrigger}
              onClick={toggleDropdown}
              aria-label={tr("ui.user_profile_menu", "User Profile Menu")}
              aria-expanded={isDropdownOpen}
            >
              <div className={styles.avatarWrapper}>
                {(avatarOverride || hasAvatar) ? (
                  <img
                    key={avatarVersion}
                    src={avatarOverride || `${user.profileimageurl!}${avatarVersion ? `?v=${avatarVersion}` : ''}`}
                    alt={fullName}
                    className={styles.avatar}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div
                    className={styles.avatar}
                    style={{
                      backgroundColor: color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FFFFFF',
                      fontWeight: 600,
                      fontSize: '16px'
                    }}
                  >
                    {initial}
                  </div>
                )}
                {isImpersonating && <div className={styles.avatarImpersonationBadge}>{tr("ui.login_as", "Login As")}</div>}
              </div>
              <div className={styles.userInfo}>
                <span className={`${styles.userName} subtitle-1`}>{user.firstname || user.username}</span>
                <span className={`${styles.userRole} subtitle-2`}>{userRole}</span>
              </div>
            </button>

            {isDropdownOpen && (
              <div className={styles.dropdownMenu}>
                {/* Phones and tablets: the header's sun/moon switch is hidden there, so the menu carries it */}
                {showThemeToggle && (
                  <div className={`${styles.dropdownSection} ${styles.menuTheme}`} role="group" aria-label={tr("ui.colour_theme", "Colour theme")}>
                    {(['light', 'dark'] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={`${styles.menuThemeOpt} ${theme === m ? styles.menuThemeActive : ''}`}
                        onClick={() => handleThemeChange(m)}
                        aria-pressed={theme === m}
                      >
                        {m === 'light' ? <ModeIcon aria-hidden="true" /> : <MoonIcon aria-hidden="true" />}
                        <span>{m === 'light' ? tr("ui.light_mode", "Light mode") : tr("ui.dark_mode", "Dark mode")}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className={styles.dropdownSection}>
                  <a href="/user/profile" className={`${styles.dropdownItem} subtitle-2`}>
                    <ProfileIcon aria-hidden="true" />
                    <span>{tr("ui.profile", "Profile")}</span>
                  </a>
                  {!isImpersonating && signsInWithPassword(user) && (
                    <a
                      href={CHANGE_PASSWORD_PATH}
                      className={`${styles.dropdownItem} subtitle-2`}
                      onClick={(e) => {
                        e.preventDefault();
                        setIsDropdownOpen(false);
                        navigate(CHANGE_PASSWORD_PATH, { state: { from: location } });
                      }}
                    >
                      <LockIcon aria-hidden="true" />
                      <span>{tr("user.pw_change.action", "Change password")}</span>
                    </a>
                  )}
                </div>

                <div className={`${styles.dropdownDivider} ${styles.lastDivider}`}></div>

                <div className={styles.dropdownSection}>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsDropdownOpen(false);
                      setIsLogoutModalOpen(true);
                    }}
                    className={`${styles.dropdownItem} ${styles.logoutBtn} subtitle-2`}
                  >
                    <LogoutIcon aria-hidden="true" />
                    <span>{tr("ui.log_out", "Log out")}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </header>

      <ConfirmModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogoutConfirm}
        title={tr("ui.log_out", "Log out")}
        description={tr("ui.are_you_sure_you_want_to_log_out", "Are you sure you want to log out?")}
        confirmText={tr("ui.log_out", "Log out")}
        cancelText={tr("common.cancel", "Cancel")}
      />
    </>
  );
};